import Request from '../../common/api/request';
import ServiceResultFactory from '../../common/api/ServiceResultFactory';

const getExhibitorDetail = (baseUrl, eventEditionId, exhibitingOrganisationId, locale) => {
  const returnFields = `exhibitingOrganisationId,
    organisationGuid,
    companyName,
    package,
    eventEditionId,
    eventEditionName,
    isEmperiaEnabled,
    exhibitorTypeId,
    entitlements{
    productCode,
    totalValue,
    productNumber
    },  
    startDate, 
    endDate, 
    logo { 
      url, 
      height, 
      width
    },
    rawStands,
    stands{
      size,
      unitOfMeasurement,
      standType,
      name
    }`;


  const query = `{
  userExhibitingOrganisation(
    eventEditionId: "${eventEditionId}",
    exhibitingOrganisationId: "${exhibitingOrganisationId}"
    locale: "${locale}")
  {
    ${returnFields}
  }
}`;

  return Request.graphqlRequest(`${baseUrl}/graphql`, query)
    .then(response => ServiceResultFactory.fromGraphqlSuccess(response, locale, 'userExhibitingOrganisation'));
};


const extractResult = (response, locale) => {
  const exhibitorDetails = ServiceResultFactory.fromGraphqlSuccess(response, locale, 'userExhibitingOrganisation');
  const adminDetails = ServiceResultFactory.fromGraphqlSuccess(response, locale, 'companyAdmins');
  return {
    exhibitorDetails,
    adminDetails
  };
};

const getExhibitorAndAdminDetail = (baseUrl, eventEditionId, exhibitingOrganisationId, organisationId, locale) => {
  const returnFields = `exhibitingOrganisationId,
    organisationGuid,
    companyName,
    package,
    eventEditionId,
    eventEditionName,
    isEmperiaEnabled,
    exhibitorTypeId,
    entitlements{
    productCode,
    totalValue,
    productNumber
    }, 
    startDate, 
    endDate, 
    logo { 
      url, 
      height, 
      width
    },
    rawStands,
    stands{
      size,
      unitOfMeasurement,
      standType,
      name
    }`;


  const query = `{
  userExhibitingOrganisation(
    eventEditionId: "${eventEditionId}",
    exhibitingOrganisationId: "${exhibitingOrganisationId}"
    locale: "${locale}")
  {
    ${returnFields}
  },
  companyAdmins(
    organisationId: "${organisationId}",
    eventEditionId: "${eventEditionId}",
    filterByLoggedInUser:true)
    {
      admins{
        userId,
        isPrimaryAdministrator,
        jobTitlesMultilingual {
          id,
          title {
            locale,
            name
          }
        }
      }
    }
}`;
  return Request.graphqlRequest(`${baseUrl}/graphql`, query)
    .then(response => extractResult(response, locale))
    .catch(result =>
      ServiceResultFactory.fromError(locale, result, false));
};

const getDigitalPortfolio = (baseUrl, locale, eventEditionId) => {
  const query = `{
        digitalPortfolioForGivenEventEditionId(eventEditionId: "${eventEditionId}")
            {
              id,
              name
            }
        }`;
  return Request.graphqlRequest(`${baseUrl}/graphql`, query)
    .then(response => ServiceResultFactory.fromGraphqlSuccess(response, locale, 'digitalPortfolioForGivenEventEditionId'))
    .catch(result =>
      ServiceResultFactory.fromError(locale, result, false));
};

const getNetworkFollowees = (baseUrl, locale, digitalPortfolioId) => {
  const query = `{
        networkFollowees(digitalPortfolioId: "${digitalPortfolioId}") {
            followeeId,
            followeeType
        }
    }`;
  return Request.graphqlRequest(`${baseUrl}/graphql`, query)
    .then(response => ServiceResultFactory.fromGraphqlSuccess(response, locale, 'networkFollowees'));
};

const getFollowedOrganisations = (baseUrl, locale, digitalPortfolioId) => getNetworkFollowees(baseUrl, locale, digitalPortfolioId)
  .then(result => result.networkFollowees?.filter(item => item.followeeType === 'Organisation').map(org => org.followeeId))
  .catch(result =>
    ServiceResultFactory.fromError(locale, result, false));

// The networking API identifies the digital portfolio by the event edition id.
const getDigitalPortfolioWithFollowedOrganisations = (baseUrl, locale, eventEditionId) => {
  if (!eventEditionId) {
    return Promise.resolve(null);
  }
  return getFollowedOrganisations(baseUrl, locale, eventEditionId)
    .then(followedOrganisations => ({
      digitalPortfolioId: eventEditionId,
      networkFollowees: Array.isArray(followedOrganisations) ? followedOrganisations : []
    }))
    .catch(() => null);
};

const followOrUnfollowOrganisation = (baseUrl, locale, digitalPortfolioId, organisationId, action) => {
  if (!action) {
    const mutation = `
    mutation {
        followFollowee(
          followRequest: {
            digitalPortfolioId: "${digitalPortfolioId}",
            followeeId: "${organisationId}",
            followeeType: "Organisation"
          }
        )
      }`;
    return Request.graphqlRequest(`${baseUrl}/graphql`, mutation)
      .then(response => response.body.data.followFollowee)
      .catch(result =>
        ServiceResultFactory.fromError(locale, result, false));
  }
  const mutation = `
      mutation {
        unfollowFollowee(
            followRequest: {
              digitalPortfolioId: "${digitalPortfolioId}",
              followeeId: "${organisationId}"
            }
          )
        }`;
  return Request.graphqlRequest(`${baseUrl}/graphql`, mutation)
    .then(response => response.body.data.unfollowFollowee)
    .catch(result =>
      ServiceResultFactory.fromError(locale, result, false));
};

const getProfileCompletenessScore = (baseUrl, locale, exhibitingOrganisationId) => {
    const query = `{
    profileCompleteness(exhibitingOrganisationId: "${exhibitingOrganisationId}")
    {
      percentageCompleted
    }
  }`;
  return Request.graphqlRequest(`${baseUrl}/graphql`, query)
  .then(response => ServiceResultFactory.fromGraphql(response, locale, 'profileCompleteness'))
  .catch(result => ServiceResultFactory.fromError(locale, result, false));
};

export default {
  getExhibitorDetail,
  getExhibitorAndAdminDetail,
  getDigitalPortfolio,
  getNetworkFollowees,
  getFollowedOrganisations,
  getDigitalPortfolioWithFollowedOrganisations,
  followOrUnfollowOrganisation,
  getProfileCompletenessScore
};
